import { ReactNode, useId } from "react"

import { Box, Stack, SxProps, Theme, Typography } from "@mui/material"

// Icon + title, then value + phrase. With children: children, value, title, trend.
const DashboardKpiCard = ({
  accent,
  icon,
  iconBgColor = "mockup.neutral95",
  iconColor = "mockup.neutral10",
  value,
  valueColor = "mockup.neutral10",
  label,
  caption,
  trend,
  footer,
  children,
  sx,
}: {
  accent: string
  icon?: ReactNode
  iconBgColor?: string
  iconColor?: string
  value: string
  valueColor?: string
  label: string
  caption?: string
  trend?: ReactNode
  footer?: ReactNode
  children?: ReactNode
  sx?: SxProps<Theme>
}) => {
  const labelId = useId()
  const valueId = useId()
  const captionId = useId()

  const title = (
    <Typography id={labelId} variant="body1" color="mockup.neutral10">
      {label}
    </Typography>
  )
  const valueText = (
    <Typography
      variant="h2"
      component="p"
      id={valueId}
      color={valueColor}
      fontWeight={700}
    >
      {value}
    </Typography>
  )

  return (
    <Stack
      role="group"
      aria-labelledby={[labelId, valueId, caption && captionId]
        .filter(Boolean)
        .join(" ")}
      sx={[
        {
          bgcolor: "mockup.neutral100",
          border: "1px solid",
          borderColor: "mockup.neutral90",
          borderTop: "4px solid",
          borderTopColor: accent,
          borderRadius: "16px",
          p: 2,
          rowGap: 0.5,
          alignItems: "center",
          textAlign: "center",
          height: "100%",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children ? (
        <>
          {children}
          {valueText}
          {title}
        </>
      ) : (
        <>
          <Stack alignItems="center" rowGap={1} sx={{ mb: 1.5 }}>
            {icon && (
              <Box
                aria-hidden
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: "8px",
                  bgcolor: iconBgColor,
                  color: iconColor,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {icon}
              </Box>
            )}
            {title}
          </Stack>
          {valueText}
          {caption && (
            <Typography
              id={captionId}
              variant="body2"
              color="mockup.neutral30"
              fontWeight={500}
            >
              {caption}
            </Typography>
          )}
        </>
      )}
      {trend}
      {footer}
    </Stack>
  )
}

export default DashboardKpiCard
